import { useRef, useState, type ChangeEvent } from 'react';
import { Button, ConfirmationModal, InputWrapper } from '@insio/ui-toolkit';
import { Permission } from '@insio/shared-types';
import { usePermissions } from '@/features/auth';
import { attachmentDownloadUrl } from '@/services/attachment.service';
import {
  ACCOUNT_LOGO_ACCEPT,
  MAX_ACCOUNT_LOGO_SIZE_MB,
  useOrganizationLogo
} from './use-organization-logo';

interface OrganizationLogoSectionProps {
  /** Node ID of the account the logo belongs to. */
  readonly accountId: string;
  readonly logo: { readonly id: string; readonly filename: string } | null;
}

const INPUT_ID = 'org-logo';

/** The organization logo: preview, plus upload, replace and remove for whoever edits the account. */
export function OrganizationLogoSection({ accountId, logo }: OrganizationLogoSectionProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.ACCOUNT_SETTINGS);
  const { t, busy, upload, remove } = useOrganizationLogo(accountId, logo?.id ?? null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared, so picking the same file again after a refusal still fires a change.
    event.target.value = '';
    if (file) void upload(file);
  };

  const handleRemove = async () => {
    await remove();
    setConfirmingRemove(false);
  };

  return (
    <InputWrapper
      label={t('label')}
      htmlFor={canEdit ? INPUT_ID : undefined}
      labelSize='sm'
      hint={canEdit ? t('hint', { max: MAX_ACCOUNT_LOGO_SIZE_MB }) : undefined}
    >
      <div className='flex flex-wrap items-center gap-md' data-testid='organization-logo'>
        {logo ? (
          <div className='flex h-20 w-40 items-center justify-center rounded-lg border border-border-base bg-background-input p-xs'>
            <img
              src={attachmentDownloadUrl(accountId, logo.id)}
              alt={t('alt')}
              className='max-h-full max-w-full object-contain'
              data-testid='organization-logo-preview'
            />
          </div>
        ) : (
          !canEdit && <p className='text-sm text-foreground-muted'>{t('none')}</p>
        )}

        {canEdit && (
          <div className='flex gap-sm'>
            <input
              ref={fileInput}
              id={INPUT_ID}
              type='file'
              accept={ACCOUNT_LOGO_ACCEPT}
              className='hidden'
              onChange={handleFile}
              data-testid='organization-logo-input'
            />
            <Button
              variant='default'
              outline='default'
              size='sm'
              disabled={busy}
              onClick={() => fileInput.current?.click()}
              testId='organization-logo-upload'
            >
              {logo ? t('replace') : t('upload')}
            </Button>
            {logo && (
              <Button
                variant='ghost'
                size='sm'
                disabled={busy}
                onClick={() => setConfirmingRemove(true)}
                testId='organization-logo-remove'
              >
                {t('remove')}
              </Button>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        open={confirmingRemove}
        title={t('removeTitle')}
        message={t('removeMessage')}
        confirmLabel={t('remove')}
        cancelLabel={t('cancel')}
        variant='danger'
        loading={busy}
        onConfirm={() => void handleRemove()}
        onClose={() => setConfirmingRemove(false)}
        testId='organization-logo-remove-confirm'
      />
    </InputWrapper>
  );
}
